import { Controller } from "@hotwired/stimulus";

/**
 * Syncs the nu_published_at date field with the nu_status select.
 *
 * When the status changes to "published", the published-at container is shown
 * and the date field is set to today (if currently blank). When the status
 * changes to "draft", the date field is cleared and the container is hidden.
 *
 * The date input's max attribute is always set to today so future dates
 * cannot be selected.
 *
 * Usage:
 *   <div data-controller="forms--status-published-at">
 *     <select data-forms--status-published-at-target="status"
 *             data-action="change->forms--status-published-at#statusChanged">
 *     </select>
 *     <div data-forms--status-published-at-target="publishedAtContainer">
 *       <input type="date" data-forms--status-published-at-target="publishedAt">
 *     </div>
 *   </div>
 */
export default class extends Controller {
  static targets = ["status", "publishedAt", "publishedAtContainer"];

  static values = {
    publishedStatus: { type: String, default: "published" },
  };

  connect() {
    this.publishedAtTarget.max = this.todayISO();
    this.togglePublishedAt();
  }

  statusChanged() {
    const status = this.statusTarget.value;

    if (status === this.publishedStatusValue) {
      if (!this.publishedAtTarget.value) {
        this.publishedAtTarget.value = this.todayISO();
      }
    } else {
      this.publishedAtTarget.value = "";
    }

    this.togglePublishedAt();
  }

  /**
   * Shows or hides the published-at container based on the current status.
   */
  togglePublishedAt() {
    const isPublished = this.statusTarget.value === this.publishedStatusValue;

    this.publishedAtContainerTarget.classList.toggle("d-none", !isPublished);
  }

  /**
   * Returns today's date as an ISO 8601 string (YYYY-MM-DD), which is the
   * format expected by <input type="date">.
   */
  todayISO() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
}
